import React from 'react'

import { useDeleteCourseV2ApplyMutation } from '@core/app/api/applyJobV2Api'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2Card from '@core/components/ApplyDialogV2/ApplyV2Card'
import ApplyV2CoursesStepAdd from '@core/components/ApplyDialogV2/ApplyV2CoursesStep/ApplyV2CoursesStepAdd'
import ApplyV2CoursesStepEdit from '@core/components/ApplyDialogV2/ApplyV2CoursesStep/ApplyV2CoursesStepEdit'
import { CoursesEmptyIcon } from '@core/components/ApplyDialogV2/ApplyV2CoursesStep/icons/CoursesEmptyIcon'
import ApplyV2NoFieldsSwitch from '@core/components/ApplyDialogV2/ApplyV2NoFieldsSwitch'
import { getApplyFilePreviewAuth, useFilePreviewSession } from '@core/components/FilePreview'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import { useFilePreview } from '@core/utils/contexts/FilePreviewContext/FilePreviewContext'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'

import './ApplyV2CoursesStep.scss'

const baseClass = 'ApplyV2CoursesStep'

function ApplyV2CoursesStep() {
    const { postProfileData, isPostProfileLoading, isPostApplied, settingsData, onNextStep, postProfileRefetch } =
        useApplyV2Context()
    const { getTitle } = useApplyV2NavTranslations()
    const { openPreview } = useFilePreview()
    const { openFilePreview } = useFilePreviewSession()
    const [itemLoading, setItemLoading] = React.useState<string | number | null>(null)
    const [editState, setEditState] = React.useState<'add' | string | null>(null)
    const [isNoItems, setIsNoItems] = React.useState(false)
    const [deleteCourseRequest, params] = useDeleteCourseV2ApplyMutation()

    const course = postProfileData?.data?.course || []
    const uid = postProfileData?.data?.uid ?? ''

    const handleDeleteCourseRequest = async (uid: string) => {
        try {
            setItemLoading(uid)
            await deleteCourseRequest({
                postUid: postProfileData?.data?.uid || '',
                courseUid: uid,
            }).unwrap()
            await postProfileRefetch()
        } catch (error) {
            // console.log('Error deleting course:', error)
        } finally {
            setItemLoading(null)
        }
    }

    const getIsLoading = (uid?: string | number | null) => {
        return itemLoading === uid
    }

    const updatedCourse = course.find((c) => c.uid === editState || c.existingId === editState)

    if (editState === 'add') {
        return <ApplyV2CoursesStepAdd onClose={() => setEditState(null)} />
    }

    if (editState && editState.length > 3 && editState !== 'add' && updatedCourse) {
        return <ApplyV2CoursesStepEdit onClose={() => setEditState(null)} item={updatedCourse} />
    }

    return (
        <div className={baseClass}>
            <div className={`${baseClass}__container`}>
                {course.length === 0 ? (
                    <div className={`${baseClass}__items ${baseClass}__empty`}>
                        <div className={` ${baseClass}__empty--icon`}>
                            <CoursesEmptyIcon />
                        </div>
                        <div className={` ${baseClass}__empty--desc`}>
                            <div className={` ${baseClass}__empty--desc-title`}>
                                {isNoItems
                                    ? getTitle('10306', "I don't have any courses/certificates")
                                    : getTitle('10326', 'No courses / certificates added yet')}
                            </div>
                            <div className={` ${baseClass}__empty--desc-btns`}>
                                <ApplyV2Button
                                    variant={'outline'}
                                    size={'small'}
                                    className={` ${baseClass}__empty--desc-btns--add`}
                                    onClick={() => {
                                        setEditState('add')
                                    }}
                                    isDisabled={isNoItems}
                                    leftIcon={<i className="fa-regular fa-plus"></i>}
                                >
                                    {/*{getTitle('1533', 'Add course/certification')}*/}
                                    {getTitle('10325', 'Add items')}
                                </ApplyV2Button>
                                {/*<ApplyV2Switch*/}
                                {/*    checked={isNoItems}*/}
                                {/*    onChange={(e) => {*/}
                                {/*        setIsNoItems(e.target.checked)*/}
                                {/*        // if (e.target.checked) {*/}
                                {/*        //     setEditState(null)*/}
                                {/*        // } else {*/}
                                {/*        //     setEditState('add')*/}
                                {/*        // }*/}
                                {/*    }}*/}
                                {/*>*/}
                                {/*    {getTitle('9557', 'No items')}*/}
                                {/*</ApplyV2Switch>*/}
                                <ApplyV2NoFieldsSwitch
                                    className={`${baseClass}__empty--desc-switch`}
                                    checked={isNoItems}
                                    onChange={(v) => setIsNoItems(v)}
                                    label={getTitle('11822', 'No courses')}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={`${baseClass}__items`}>
                        {course.map((item) => (
                            <ApplySkeleton
                                isLoading={getIsLoading(item.uid || item.existingId)}
                                key={item.uid ?? item.existingId}
                                style={{
                                    height: '150px',
                                    marginBottom: '1rem',
                                    display: 'block',
                                    width: '100%',
                                }}
                            >
                                <ApplyV2Card
                                    // key={item.uid ?? item.existingId}
                                    className={`${baseClass}__items--course`}
                                    title={decodeHtmlEntitiesRecursive(item.courseName)}
                                    files={item.files}
                                    onDelete={() => {
                                        handleDeleteCourseRequest(item.uid)
                                    }}
                                    onEdit={() => setEditState(item.uid)}
                                    date={{
                                        startDate: item.startDate,
                                        endDate: item.endDate,
                                        dateFormat: 'D MMM YYYY',
                                        showMonths: true,
                                        isShowPresent: false,
                                    }}
                                    onFileClick={(f) => {
                                        console.log(f)
                                        const previewAuth = getApplyFilePreviewAuth(f, uid)
                                        if (previewAuth) {
                                            openFilePreview(previewAuth)
                                        } else {
                                            openPreview(
                                                {
                                                    Filename: f.name,
                                                    Ext: f.ext,
                                                    CandidateFileID: f.existingId,
                                                    downloadUrl: f.downloadUrl,
                                                    Size: f.size,
                                                    ...f,
                                                    uid: uid,
                                                    fileUid: f.uid,
                                                    type: f.type,
                                                },
                                                f.folder
                                            )
                                        }
                                    }}
                                    // dateSection={
                                    //     (item.startDate || item.endDate) && (
                                    //         <div className={`${baseClass}__items--course-period`}>
                                    //             <span>
                                    //                 {formatDateRange(item.startDate, item.endDate, {
                                    //                     dateFormat: 'D MMM YYYY',
                                    //                 })}
                                    //             </span>
                                    //             {formatDuration(item.startDate, item.endDate, {
                                    //                 showMonths: true,
                                    //             }) && (
                                    //                 <span className={`${baseClass}__items--course-period-total`}>
                                    //                     {` (${formatDuration(item.startDate, item.endDate, {
                                    //                         showMonths: true,
                                    //                     })})`}
                                    //                 </span>
                                    //             )}
                                    //         </div>
                                    //     )
                                    // }
                                >
                                    {item.courseDescription}
                                </ApplyV2Card>
                            </ApplySkeleton>
                        ))}
                        {isPostProfileLoading && !itemLoading && (
                            <ApplySkeleton
                                isLoading={isPostProfileLoading}
                                style={{
                                    height: '150px',
                                    marginBottom: '1rem',
                                    display: 'block',
                                    width: '100%',
                                }}
                            />
                        )}
                        <ApplyV2Button
                            variant={'transparent'}
                            className={`${baseClass}__addBtn`}
                            onClick={() => {
                                setEditState('add')
                            }}
                            leftIcon={
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M7.99967 1.33301C8.36786 1.33301 8.66634 1.63148 8.66634 1.99967V7.12611H13.9997C14.3679 7.12611 14.6663 7.42459 14.6663 7.79278C14.6663 8.16097 14.3679 8.45944 13.9997 8.45944H8.66634V13.9997C8.66634 14.3679 8.36786 14.6663 7.99967 14.6663C7.63148 14.6663 7.33301 14.3679 7.33301 13.9997V8.45944H1.99967C1.63148 8.45944 1.33301 8.16097 1.33301 7.79278C1.33301 7.42459 1.63148 7.12611 1.99967 7.12611H7.33301V1.99967C7.33301 1.63148 7.63148 1.33301 7.99967 1.33301Z"
                                        fill="#19191A"
                                    />
                                </svg>
                            }
                        >
                            {getTitle('1533', 'Add course/certification')}
                        </ApplyV2Button>
                    </div>
                )}
            </div>
            <div className={`ApplyDialogV2__next ${baseClass}__next`}>
                {!settingsData?.steps?.course?.required && (
                    <ApplyV2Button
                        variant={'transparent'}
                        className={`ApplyDialogV2__next--btn ApplyDialogV2__next--btn-skip`}
                        rightIcon={'>'}
                        // isDisabled={!isValid || isLoading}
                        onClick={() => {
                            onNextStep()
                        }}
                    >
                        {getTitle('6107', 'Skip')}
                    </ApplyV2Button>
                )}
                <ApplyV2Button
                    variant={'primary'}
                    className={`ApplyDialogV2__next--btn ApplyDialogV2__next--btn-submit`}
                    onClick={onNextStep}
                    isLoading={isPostApplied || isPostProfileLoading}
                    isDisabled={isPostApplied || isPostProfileLoading || (!isNoItems && course.length === 0)}
                    type={'submit'}
                >
                    {getTitle('3035', 'Next')}
                </ApplyV2Button>
            </div>
        </div>
    )
}

export default ApplyV2CoursesStep
